import React from 'react';
import { BarChart3, TrendingUp, Users, MessageCircle, Activity } from 'lucide-react';
import Icon from '../../../components/AppIcon';

const InteractiveDemo = ({ featureType, usageData }) => {
  const renderTrackingDemo = () => (
    <div className="space-y-4">
      <div className="bg-muted/30 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-semibold text-foreground">Basic Plan Limits</h4>
          <span className="text-sm text-destructive font-semibold">Limited</span>
        </div>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Daily Check-ins</span>
            <span className="text-sm font-semibold">3 / 3 used</span>
          </div>
          <div className="w-full bg-border rounded-full h-2">
            <div className="bg-destructive h-2 rounded-full" style={{ width: '100%' }} />
          </div>
          <p className="text-xs text-destructive">Limit reached - Upgrade to continue</p>
        </div>
      </div>

      <div className="bg-success/10 rounded-lg p-6 border-2 border-success">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-semibold text-foreground">Premium Plan</h4>
          <span className="text-sm text-success font-semibold">Unlimited</span>
        </div>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Daily Check-ins</span>
            <span className="text-sm font-semibold">∞ Unlimited</span>
          </div>
          <div className="w-full bg-success/20 rounded-full h-2">
            <div className="bg-success h-2 rounded-full" style={{ width: '45%' }} />
          </div>
          <p className="text-xs text-success">Track as much as you need, whenever you need</p>
        </div>
      </div>
    </div>
  );

  const renderAnalyticsDemo = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-primary/10 rounded-lg p-4 border border-primary/20">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-5 h-5 text-primary" />
            <span className="text-sm font-semibold text-foreground">Pattern Detection</span>
          </div>
          <p className="text-2xl font-bold text-foreground">87%</p>
          <p className="text-xs text-muted-foreground">Accuracy rate</p>
        </div>
        <div className="bg-success/10 rounded-lg p-4 border border-success/20">
          <div className="flex items-center gap-2 mb-2">
            <BarChart3 className="w-5 h-5 text-success" />
            <span className="text-sm font-semibold text-foreground">Correlations Found</span>
          </div>
          <p className="text-2xl font-bold text-foreground">12</p>
          <p className="text-xs text-muted-foreground">This month</p>
        </div>
        <div className="bg-warning/10 rounded-lg p-4 border border-warning/20">
          <div className="flex items-center gap-2 mb-2">
            <Activity className="w-5 h-5 text-warning" />
            <span className="text-sm font-semibold text-foreground">Predictions</span>
          </div>
          <p className="text-2xl font-bold text-foreground">5</p>
          <p className="text-xs text-muted-foreground">Active insights</p>
        </div>
      </div>
      <div className="bg-card rounded-lg p-4 border border-border">
        <p className="text-sm text-muted-foreground mb-2">Sample Insight:</p>
        <p className="text-foreground font-medium">
          "Your headache symptoms show a 78% correlation with stress levels. Consider stress management techniques on high-stress days."
        </p>
      </div>
    </div>
  );

  const renderCaregiverDemo = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-card rounded-lg p-4 border border-border">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Users className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="font-semibold text-foreground">Dr. Sarah Johnson</p>
              <p className="text-xs text-muted-foreground">Primary Caregiver</p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs text-success">
            <div className="w-2 h-2 rounded-full bg-success" />
            <span>Active • Last viewed 2 hours ago</span>
          </div>
        </div>
        <div className="bg-card rounded-lg p-4 border border-border">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
              <Users className="w-5 h-5 text-accent" />
            </div>
            <div>
              <p className="font-semibold text-foreground">Mom</p>
              <p className="text-xs text-muted-foreground">Family Caregiver</p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs text-success">
            <div className="w-2 h-2 rounded-full bg-success" />
            <span>Active • Last viewed 5 minutes ago</span>
          </div>
        </div>
      </div>
      <div className="bg-warning/10 rounded-lg p-4 border border-warning/20">
        <div className="flex items-center gap-2 mb-2">
          <Icon name="Bell" size={20} className="text-warning" />
          <span className="font-semibold text-foreground">Real-time Alerts</span>
        </div>
        <p className="text-sm text-muted-foreground">
          Caregivers receive instant notifications when you log severe symptoms or miss check-ins.
        </p>
      </div>
    </div>
  );

  const renderSupportDemo = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-muted/30 rounded-lg p-4">
          <h4 className="font-semibold text-foreground mb-3">Basic Support</h4>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground" />
              Email support only
            </li>
            <li className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground" />
              48-hour response time
            </li>
            <li className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground" />
              Business hours only
            </li>
          </ul>
        </div>
        <div className="bg-success/10 rounded-lg p-4 border-2 border-success">
          <h4 className="font-semibold text-foreground mb-3">Premium Support</h4>
          <ul className="space-y-2 text-sm text-foreground">
            <li className="flex items-center gap-2">
              <Icon name="Check" size={16} className="text-success" />
              24/7 email & live chat
            </li>
            <li className="flex items-center gap-2">
              <Icon name="Check" size={16} className="text-success" />
              2-hour response guarantee
            </li>
            <li className="flex items-center gap-2">
              <Icon name="Check" size={16} className="text-success" />
              Dedicated account manager
            </li>
          </ul>
        </div>
      </div>
      <div className="bg-card rounded-lg p-4 border border-border">
        <div className="flex items-center gap-3">
          <MessageCircle className="w-8 h-8 text-primary" />
          <div>
            <p className="font-semibold text-foreground">Average Response Time</p>
            <p className="text-2xl font-bold text-success">1.5 hours</p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderDemo = () => {
    switch (featureType) {
      case 'unlimited_tracking':
        return renderTrackingDemo();
      case 'advanced_analytics':
        return renderAnalyticsDemo();
      case 'caregiver_tools':
        return renderCaregiverDemo();
      case 'priority_support':
        return renderSupportDemo();
      default:
        return renderTrackingDemo();
    }
  };

  return (
    <div className="space-y-4">
      {renderDemo()}
    </div>
  );
};

export default InteractiveDemo;